import React from 'react'
import { motion } from 'framer-motion'
import { Download, Zap, Shield, Globe, ArrowRight, Play } from 'lucide-react'

const Hero: React.FC = () => {
  const handleDownload = () => {
    // 滚动到下载区域
    document.getElementById('download')?.scrollIntoView({ behavior: 'smooth' })
  }

  const handleLearnMore = () => {
    // 滚动到功能区域
    document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
      {/* 背景渐变 */}
      <div className="absolute inset-0 gradient-bg-primary opacity-90"></div>
      
      {/* 动态背景元素 */}
      <div className="absolute inset-0">
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 180, 360],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute top-20 left-10 w-32 h-32 bg-white/10 rounded-full blur-xl"
        />
        <motion.div
          animate={{
            scale: [1.2, 1, 1.2],
            rotate: [360, 180, 0],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute bottom-20 right-10 w-40 h-40 bg-purple-500/20 rounded-full blur-xl"
        />
        <motion.div
          animate={{
            y: [0, -20, 0],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-1/2 left-1/4 w-24 h-24 bg-blue-500/20 rounded-full blur-lg"
        />
      </div>

      {/* 主要内容 */}
      <div className="container-responsive relative z-10">
        <div className="text-center space-y-8">
          {/* 主标题 */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-4"
          >
            <h1 className="mobile-heading font-bold text-white leading-tight">
              <span className="block">暴风加速器</span>
              <span className="block text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-light">
                极速VPN体验
              </span>
            </h1>
            <p className="mobile-text text-blue-100 max-w-2xl mx-auto leading-relaxed">
              3.5G超高速带宽，99.9%稳定连接，保护您的网络隐私，
              <br className="hidden sm:block" />
              让您畅游全球网络，访问YouTube、Instagram、Facebook等平台
            </p>
          </motion.div>

          {/* 核心特性 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex flex-wrap justify-center gap-6 text-white/90"
          >
            <div className="flex items-center space-x-2">
              <Zap className="w-5 h-5 text-yellow-400" />
              <span className="font-medium">3.5G带宽</span>
            </div>
            <div className="flex items-center space-x-2">
              <Shield className="w-5 h-5 text-green-400" />
              <span className="font-medium">隐私保护</span>
            </div>
            <div className="flex items-center space-x-2">
              <Globe className="w-5 h-5 text-blue-400" />
              <span className="font-medium">全球覆盖</span>
            </div>
          </motion.div>

          {/* 行动按钮 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <button
              onClick={handleDownload}
              className="btn-large bg-white text-blue-600 hover:bg-gray-50 flex items-center justify-center space-x-2 group"
            >
              <Download className="w-5 h-5" />
              <span>查看下载教程</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={handleLearnMore}
              className="btn-large bg-transparent text-white border-2 border-white/30 hover:bg-white/10 flex items-center justify-center space-x-2"
            >
              <Play className="w-4 h-4" />
              <span>了解更多</span>
            </button>
          </motion.div>

          {/* 信任标识 */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="pt-8"
          >
            <p className="text-white/70 text-sm mb-4">已服务超过 100万+ 用户</p>
            <div className="flex justify-center space-x-8 text-white/50">
              <div className="text-center">
                <div className="text-2xl font-bold">99.9%</div>
                <div className="text-xs">可用性</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">50+</div>
                <div className="text-xs">国家地区</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">24/7</div>
                <div className="text-xs">技术支持</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* 滚动指示器 */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 1 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center"
        >
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-1 h-3 bg-white/60 rounded-full mt-2"
          />
        </motion.div>
      </motion.div>
    </section>
  )
}

export default Hero
